/**
 * Team Utility Functions
 * 
 * Provides helper functions for working with teams and team claiming.
 * Handles the nested structure: teams[leagueId][seasonId] = teamId or { teamId, claimedAt }
 */

/**
 * Get team by ID
 * @param {string} teamId - Team ID
 * @param {Array} teams - Array of team objects with id and name
 * @returns {Object|null} - Team object or null if not found
 */
export function getTeamById(teamId, teams) {
  if (!teamId || !teams || teams.length === 0) return null;
  return teams.find(t => t.id === teamId) || null;
}

/**
 * Get team name from ID
 * @param {string} teamId - Team ID
 * @param {Array} teams - Array of team objects with id and name
 * @returns {string} - Team name or 'Unknown Team' if not found
 */
export function getTeamName(teamId, teams) {
  if (!teamId) return 'Unknown Team';
  const team = getTeamById(teamId, teams);
  return team ? team.name : 'Unknown Team';
}

/**
 * Normalize to team ID (handles both ID and name formats)
 * @param {string} value - Either a teamId or teamName
 * @param {Array} teams - Array of team objects with id and name
 * @returns {string|null} - Team ID or null if not found
 */
export function normalizeToTeamId(value, teams) {
  if (!value || !teams || teams.length === 0) return null;
  
  // If it's already an ID (matches a team.id), return it
  const byId = teams.find(t => t.id === value);
  if (byId) return byId.id;
  
  // If it's a name, find the ID
  const byName = teams.find(t => t.name === value);
  return byName ? byName.id : null;
}

/**
 * Extract team ID from nested teams structure value
 * Handles both old format (string) and new format (object with teamId and claimedAt)
 * @param {string|Object} value - Either a teamId string or { teamId, claimedAt } object
 * @returns {string|null} - Team ID or null
 */
export function extractTeamIdFromValue(value) {
  if (!value) return null;
  
  // If it's already a string (old format), return it
  if (typeof value === 'string') return value;
  
  // If it's an object with teamId property (new format), return teamId
  if (typeof value === 'object' && value.teamId) return value.teamId;
  
  return null;
}

/**
 * Extract claimedAt timestamp from nested teams structure value
 * @param {string|Object} value - Either a teamId string or { teamId, claimedAt } object
 * @returns {Date|timestamp|null} - ClaimedAt timestamp or null
 */
export function extractClaimedAtFromValue(value) {
  if (!value) return null;
  
  // If it's a string (old format), no claimedAt available
  if (typeof value === 'string') return null;
  
  // If it's an object with claimedAt property (new format), return it
  if (typeof value === 'object' && value.claimedAt) return value.claimedAt;
  
  return null;
}

/**
 * Get all teams claimed by a user from nested teams structure
 * @param {Object} userTeams - User's teams object: { leagueId: { seasonId: teamId or { teamId, claimedAt } } }
 * @returns {Array} - Array of { teamId, leagueId, seasonId, claimedAt } objects
 */
export function getUserClaimedTeams(userTeams) {
  if (!userTeams || typeof userTeams !== 'object') return [];
  
  const claimedTeams = [];
  
  // Iterate through leagues
  for (const leagueId in userTeams) {
    if (userTeams.hasOwnProperty(leagueId)) {
      const leagueData = userTeams[leagueId];
      
      // Iterate through seasons
      if (leagueData && typeof leagueData === 'object') {
        for (const seasonId in leagueData) {
          if (leagueData.hasOwnProperty(seasonId)) {
            const value = leagueData[seasonId];
            
            // Handle array format (multiple teams per league-season)
            if (Array.isArray(value)) {
              value.forEach(teamValue => {
                const teamId = extractTeamIdFromValue(teamValue);
                const claimedAt = extractClaimedAtFromValue(teamValue);
                
                if (teamId) {
                  claimedTeams.push({
                    teamId,
                    leagueId,
                    seasonId,
                    claimedAt
                  });
                }
              });
            } else {
              // Handle single team format (old format or single team)
              const teamId = extractTeamIdFromValue(value);
              const claimedAt = extractClaimedAtFromValue(value);
              
              if (teamId) {
                claimedTeams.push({
                  teamId,
                  leagueId,
                  seasonId,
                  claimedAt
                });
              }
            }
          }
        }
      }
    }
  }
  
  return claimedTeams;
}

/**
 * Check if user has claimed a specific team
 * @param {Object} userTeams - User's teams object
 * @param {string} teamId - Team ID to check
 * @param {string} leagueId - League ID (optional, for faster lookup)
 * @param {string} seasonId - Season ID (optional, for faster lookup)
 * @returns {boolean} - True if user has claimed the team
 */
export function hasUserClaimedTeam(userTeams, teamId, leagueId = null, seasonId = null) {
  if (!userTeams || !teamId) return false;
  
  // If leagueId and seasonId provided, check specific location
  if (leagueId && seasonId) {
    const leagueData = userTeams[leagueId];
    if (!leagueData) return false;

    const value = leagueData[seasonId];
    if (Array.isArray(value)) {
      return value.some((item) => extractTeamIdFromValue(item) === teamId);
    }
    const foundTeamId = extractTeamIdFromValue(value);
    return foundTeamId === teamId;
  }
  
  // Otherwise, search all leagues and seasons
  const claimedTeams = getUserClaimedTeams(userTeams);
  return claimedTeams.some(ct => ct.teamId === teamId);
}

/**
 * Get teams for a specific league from user's teams structure
 * @param {Object} userTeams - User's teams object
 * @param {string} leagueId - League ID
 * @returns {Array} - Array of { teamId, seasonId, claimedAt } objects
 */
export function getUserTeamsForLeague(userTeams, leagueId) {
  if (!userTeams || !leagueId) return [];
  
  const leagueData = userTeams[leagueId];
  if (!leagueData || typeof leagueData !== 'object') return [];
  
  const teams = [];
  
  for (const seasonId in leagueData) {
    if (leagueData.hasOwnProperty(seasonId)) {
      const value = leagueData[seasonId];
      const teamId = extractTeamIdFromValue(value);
      const claimedAt = extractClaimedAtFromValue(value);
      
      if (teamId) {
        teams.push({
          teamId,
          seasonId,
          claimedAt
        });
      }
    }
  }
  
  return teams;
}

/**
 * Get teams for a specific league-season from user's teams structure
 * @param {Object} userTeams - User's teams object
 * @param {string} leagueId - League ID
 * @param {string} seasonId - Season ID
 * @returns {Array} - Array of team IDs (should typically be one team, but supports multiple)
 */
export function getUserTeamsForLeagueSeason(userTeams, leagueId, seasonId) {
  if (!userTeams || !leagueId || !seasonId) return [];
  
  const leagueData = userTeams[leagueId];
  if (!leagueData) return [];
  
  const seasonData = leagueData[seasonId];
  if (!seasonData) return [];
  
  // Handle both old format (string) and new format (object or array)
  if (typeof seasonData === 'string') {
    return [seasonData];
  }
  
  if (Array.isArray(seasonData)) {
    return seasonData.map(item => extractTeamIdFromValue(item)).filter(Boolean);
  }
  
  if (typeof seasonData === 'object' && seasonData.teamId) {
    return [seasonData.teamId];
  }
  
  return [];
}

